import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle } from "lucide-react";
import { hardenedSubmit, generateConfirmation } from "@/lib/submit";

const inputCls = "w-full h-11 px-3 rounded-md border border-amber-500/30 bg-[#0f1629] text-white text-sm focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400/40 transition-colors";
const selectCls = "w-full h-11 px-3 rounded-md border border-amber-500/30 bg-[#0f1629] text-white text-sm focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400/40 appearance-none transition-colors";
const labelCls = "block text-[11px] font-bold text-amber-400/90 uppercase tracking-wider mb-1.5";

export default function BusinessFundingPage() {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [confNum, setConfNum] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const [form, setForm] = useState({
    business_name: "", contact_name: "", phone: "", email: "",
    business_state: "", business_industry: "", amount_requested: "",
    time_in_business: "", average_monthly_revenue: "", additional_comments: "",
  });

  const set = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!consent) { toast.error("Please accept the authorization consent."); return; }
    setLoading(true);
    setSubmitError("");
    try {
      const result = await hardenedSubmit("/business-funding", {
        ...form,
        authorization_consent: true,
        _client_confirmation: generateConfirmation(),
      });
      setConfNum(result.confirmation_number);
      setSuccess(true);
      toast.success("Application submitted successfully!");
    } catch (err) {
      setSubmitError(err.message || "Submission failed. Please try again.");
      toast.error(err.message || "Submission failed.");
      setLoading(false);
      return;
    }
    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 bg-[#0a0e1a]" data-testid="biz-funding-success">
        <div className="max-w-md w-full rounded-xl border border-slate-700/50 bg-[#111827]/70 p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-5">
            <CheckCircle className="w-8 h-8 text-emerald-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Thank you for your application.</h2>
          <p className="text-sm text-slate-400 mb-6">Your confirmation number is:</p>
          <div className="rounded-lg border border-amber-500/30 bg-[#0f1629] p-4 mb-6">
            <p className="text-2xl font-mono font-bold text-amber-400" data-testid="biz-funding-confirmation">{confNum}</p>
          </div>
          <p className="text-sm text-slate-400 leading-relaxed">
            A funding specialist will review your information and contact you regarding available financing options.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-10 md:py-14 px-4 bg-apply min-h-screen" data-testid="biz-funding-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white mb-2" data-testid="biz-funding-heading">Business Funding Application</h1>
          <p className="text-sm text-slate-400">Tell us about your business and funding needs.</p>
        </div>

        <div className="rounded-xl border border-slate-700/50 bg-[#111827]/70 backdrop-blur-sm p-6 sm:p-8">
          <form onSubmit={handleSubmit} data-testid="biz-funding-form">

            <h2 className="text-lg font-bold text-amber-400 italic mb-5">Business Information</h2>
            <div className="space-y-4">
              <div>
                <label className={labelCls}>Business Name *</label>
                <input data-testid="biz-business-name" className={inputCls} name="business_name" required value={form.business_name} onChange={set} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Contact Name *</label>
                  <input data-testid="biz-contact-name" className={inputCls} name="contact_name" required value={form.contact_name} onChange={set} />
                </div>
                <div>
                  <label className={labelCls}>Phone Number *</label>
                  <input data-testid="biz-phone" className={inputCls} name="phone" type="tel" required value={form.phone} onChange={set} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Email Address *</label>
                <input data-testid="biz-email" className={inputCls} name="email" type="email" required value={form.email} onChange={set} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Business State *</label>
                  <input data-testid="biz-state" className={inputCls} name="business_state" required value={form.business_state} onChange={set} />
                </div>
                <div>
                  <label className={labelCls}>Business Industry *</label>
                  <input data-testid="biz-industry" className={inputCls} name="business_industry" required value={form.business_industry} onChange={set} />
                </div>
              </div>
            </div>

            <h2 className="text-lg font-bold text-amber-400 italic mt-8 mb-5">Funding Details</h2>
            <div className="space-y-4">
              <div>
                <label className={labelCls}>Amount Requested *</label>
                <input data-testid="biz-amount" className={inputCls} name="amount_requested" required value={form.amount_requested} onChange={set} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Time In Business *</label>
                  <select data-testid="biz-time" className={selectCls} name="time_in_business" required value={form.time_in_business} onChange={set}>
                    <option value="">Select</option>
                    <option value="Less than 6 months">Less than 6 months</option>
                    <option value="6 months - 1 year">6 months - 1 year</option>
                    <option value="1 - 2 years">1 - 2 years</option>
                    <option value="2 - 5 years">2 - 5 years</option>
                    <option value="5+ years">5+ years</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Average Monthly Revenue *</label>
                  <input data-testid="biz-revenue" className={inputCls} name="average_monthly_revenue" required value={form.average_monthly_revenue} onChange={set} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Additional Comments</label>
                <textarea data-testid="biz-comments" className={`${inputCls} h-24 py-2`} name="additional_comments" value={form.additional_comments} onChange={set} />
              </div>
            </div>

            <div className="flex items-start gap-3 mt-6">
              <input type="checkbox" id="biz-consent" data-testid="biz-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-amber-500/40 accent-amber-500" />
              <label htmlFor="biz-consent" className="text-sm text-slate-400 leading-relaxed cursor-pointer">
                I authorize EasyQuickFund and its funding partners to contact me regarding available business financing options.
              </label>
            </div>

            {submitError && (
              <div data-testid="biz-submit-error" className="mt-5 px-4 py-3 rounded-lg border border-red-500/40 bg-red-500/10">
                <p className="text-sm text-red-400 font-medium">{submitError}</p>
              </div>
            )}

            <div className="mt-6 flex items-center gap-3 px-4 py-3 rounded-lg border border-amber-500/20 bg-[#151d2e]">
              <svg className="w-4 h-4 text-amber-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              <p className="text-xs text-slate-400 leading-relaxed">Your information is encrypted and securely transmitted. We never share your data with third parties.</p>
            </div>

            <button type="submit" disabled={loading} data-testid="biz-submit-btn"
              className="w-full mt-6 h-12 rounded-md bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 text-[#0a0e1a] font-bold text-sm uppercase tracking-wider transition-all disabled:opacity-50">
              {loading ? <span className="flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</span> : "SUBMIT APPLICATION"}
            </button>

            <p className="text-center text-xs text-slate-500 mt-4">
              By submitting, you agree to our <Link to="/privacy" className="text-amber-400 hover:underline">Privacy Policy</Link> and <Link to="/terms" className="text-amber-400 hover:underline">Terms of Service</Link>.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
